import {
  AddIcon,
  Box,
  Breakpoint,
  IconButton,
  Theme,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@freighthub/silhouette-core'
import { take } from 'lodash-es'
import { useObserver } from 'mobx-react-lite'
import { FC } from 'react'
import { useI18n } from 'utils/localisation'

import { FREIGHTIE_ID, TIdType, TSubscription, USER_ID } from '../SubscriptionModel'
import { MemberItem, MoreMembers } from './MemberItem'

const COMPACT_MAX_ACTOR_DISPLAY = 4
const SUPER_COMPACT_MAX_ACTOR_DISPLAY = 2
const EXPANDED_MAX_ACTOR_DISPLAY = 6

export const MEMBERS_LIST_COMPACT = 'compact'
export const MEMBERS_LIST_SUPER_COMPACT = 'super-compact'
export const MEMBERS_LIST_EXPANDED = 'expanded'

export type MembersListMode = 'compact' | 'super-compact' | 'expanded'

const getStyles = (theme: Theme) => ({
  memberListContainer: {
    display: 'flex',
    maxWidth: '600px',
    flexWrap: 'wrap',
  },
  plusButton: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.grey['500']}`,
    width: '40px',
    height: '40px',
    borderRadius: '50%',
  },
})

export interface MemberListProps {
  subscription: TSubscription
  removeSubscriber: (id: string, idType: TIdType) => void
  openDialog: () => void
  getAvatarColorPalette?: (userId: string) => number
  disableModifySubscribers?: boolean
  showDisplayName?: boolean
}

const breakpointMap = {
  [MEMBERS_LIST_SUPER_COMPACT]: {
    numberOfActors: SUPER_COMPACT_MAX_ACTOR_DISPLAY,
  },
  [MEMBERS_LIST_COMPACT]: {
    numberOfActors: COMPACT_MAX_ACTOR_DISPLAY,
  },
  [MEMBERS_LIST_EXPANDED]: {
    numberOfActors: EXPANDED_MAX_ACTOR_DISPLAY,
  },
}

export const useIsWidthUp = (breakpoint: number | Breakpoint): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.up(breakpoint))
}

export const useIsWidthDown = (breakpoint: number | Breakpoint): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.down(breakpoint))
}

export const useGetMemberListMode = (): MembersListMode => {
  const isMdDown = useIsWidthDown('md')
  const isSmUp = useIsWidthUp('sm')
  const isSmDown = useIsWidthDown('sm')

  if (isMdDown && isSmUp) {
    return MEMBERS_LIST_COMPACT
  }
  if (isSmDown) {
    return MEMBERS_LIST_SUPER_COMPACT
  }
  return MEMBERS_LIST_EXPANDED
}

export const getPositive = (num: number) => {
  return num > 0 ? num : 0
}

export const MembersList: FC<MemberListProps> = ({
  subscription,
  disableModifySubscribers,
  removeSubscriber,
  openDialog,
  showDisplayName,
}) => {
  const { translate } = useI18n()
  const styles = getStyles(useTheme())

  const mode = useGetMemberListMode()
  const isUserDisabled = subscription.isUserDisabled || disableModifySubscribers
  const { numberOfActors } = breakpointMap[mode]
  const { users, freighties } = subscription.subscribers
  const extraActorsNumber = getPositive(users.length + freighties.length - numberOfActors)
  const usersLengthToDisplay = users.length < numberOfActors ? users.length : numberOfActors
  const freightiesLengthToDisplay: number = numberOfActors - usersLengthToDisplay

  return useObserver(() => (
    <Box sx={styles.memberListContainer}>
      {take(users, usersLengthToDisplay).map((user) => (
        <MemberItem
          key={user._id}
          actor={user}
          idType={USER_ID}
          isDisabled={isUserDisabled!}
          removeSubscriber={removeSubscriber}
          showDisplayName={showDisplayName}
          userType="customer"
        />
      ))}
      {take(freighties, freightiesLengthToDisplay).map((freightie) => (
        <MemberItem
          key={freightie._id}
          actor={freightie}
          idType={FREIGHTIE_ID}
          isDisabled
          removeSubscriber={removeSubscriber}
          showDisplayName={showDisplayName}
          userType="fortie"
        />
      ))}
      {extraActorsNumber > 0 && <MoreMembers moreNumber={extraActorsNumber} />}
      {!disableModifySubscribers && (
        <Box width={40} display="flex" justifyContent="center">
          <Tooltip title={translate('members-list-add_more')} placement="bottom">
            <IconButton sx={styles.plusButton} onClick={openDialog} data-tracking-id="shipment-subscribers-add">
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  ))
}
